import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectUserProfile } from 'app/pages/Layout/slice/selectors';
import { useRBAC } from './context/RBACContext';
import DeleteIcon from 'app/assets/svgIcons/DeleteIcon';
import { SideNavTooltip } from './SideNavTooltip';
import styles from './SlideOutPanel.module.scss';
import { ToastQueue } from '@adobe/react-spectrum';
import { deleteMoment } from 'app/services/momentsApi';
import { DeleteConfirmationDialog } from '../shared/DeleteConfirmationDialog/DeleteConfirmationDialog';

interface DeleteButtonProps {
  apiData?: any;
  onClose?: () => void;
}

export const DeleteButton: React.FC<DeleteButtonProps> = ({
  apiData,
  onClose
}) => {
  const [isDeleteMenuOpen, setIsDeleteMenuOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const { isAdmin } = useRBAC();
  // const loggedInUser = useSelector(selectUserProfile);

  // Check if user is the creator by comparing creator email with logged-in user
  // const isCreator = React.useMemo(() => {
  //   if (!apiData?.creator || !loggedInUser?.email) return false;
  //   return apiData.creator === loggedInUser.uid;
  // }, [apiData?.creator, loggedInUser?.email]);

  // User can delete if they are:
  // 1. Admin OR
  // 2. Creator of the RSD
  const canDelete = isAdmin || apiData?.canEdit || false;

  // Toggle delete menu
  const toggleDeleteMenu = () => {
    setIsDeleteMenuOpen(!isDeleteMenuOpen);
  };

  // Open confirmation dialog for delete RSD
  const handleDeleteRSD = () => {
    if (!apiData?.issueId || !canDelete) {
      ToastQueue.negative('Unable to delete', {
        timeout: 5000
      });
      return;
    }

    setIsDeleteMenuOpen(false);
    setShowDeleteConfirmation(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Confirmed delete RSD
  const handleConfirmDelete = async () => {
    setShowDeleteConfirmation(false);
    setIsDeleting(true);

    try {
      // Backend saveDeleteAuditHistory writes RSD_DELETE on DELETE /moments/{id}; no UI /audits POST.
      const result = await deleteMoment(apiData.issueId);

      if (result.success && result.status === 204) {
        // Show success toast
        ToastQueue.positive('RSD has been deleted', { timeout: 5000 });

        // Close the slideout panel
        if (onClose) {
          onClose();
        }
      } else {
        // Show error toast
        ToastQueue.negative('Unable to Delete RSD', { timeout: 5000 });
        console.error('Delete failed:', result.error || result.message);
      }
    } catch (error) {
      // Show error toast
      ToastQueue.negative('Unable to Delete RSD', { timeout: 5000 });
      console.error('Error deleting RSD:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Close delete menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDeleteMenuOpen &&
        deleteButtonRef.current &&
        !deleteButtonRef.current.contains(event.target as Node)
      ) {
        const deleteMenu = document.querySelector('[data-menu="delete-menu"]');
        if (deleteMenu && !deleteMenu.contains(event.target as Node)) {
          setIsDeleteMenuOpen(false);
        }
      }
    };

    if (isDeleteMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDeleteMenuOpen]);

  // Don't render if user doesn't have permission
  if (!canDelete) {
    return null;
  }

  return (
    <>
      <SideNavTooltip label="Delete RSD">
        <button
          ref={deleteButtonRef}
          className={styles.deleteButton}
          onClick={toggleDeleteMenu}
          aria-label="Delete RSD"
          disabled={isDeleting}
          style={{
            opacity: isDeleting ? 0.5 : 1,
            cursor: isDeleting ? 'not-allowed' : 'pointer'
          }}
        >
          <DeleteIcon width={18} height={18} />
          {isDeleteMenuOpen && !isDeleting && (
            <div className={styles.deleteMenu} data-menu="delete-menu">
              <div className={styles.deleteMenuItem} onClick={handleDeleteRSD}>
                Delete RSD
              </div>
            </div>
          )}
        </button>
      </SideNavTooltip>

      <DeleteConfirmationDialog
        isOpen={showDeleteConfirmation}
        title="Delete RSD?"
        message="Are you sure you want to delete this RSD?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};
